import { baseApi } from "./baseApi";
import type { QuarterKey } from "./programmeApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type TaskStatus =
  | "not_started"
  | "in_progress"
  | "submitted"
  | "graded";

export interface TaskInstruction {
  stepNumber: number;
  instruction: string;
}

export interface TaskFeedback {
  gradedBy?: { firstName: string; lastName: string; role: string } | string;
  comment: string;
  score: number;
  feedbackDate: string;
}

export interface TaskWithSubmission {
  id: string;
  quarter: QuarterKey;
  cycle?: string;
  taskNumber: number;
  title: string;
  description?: string;
  instructions: TaskInstruction[];
  minWords: number;
  tags: string[];
  availableFrom?: string;
  dueDate?: string;
  daysRemaining: number | null;
  isPublished: boolean;
  createdAt: string;
  submission: {
    id?: string;
    status: TaskStatus;
    progressPercent: number;
    draftContent?: string;
    wordCount?: number;
    attachmentUrl?: string;
    attachmentName?: string;
    isLate?: boolean;
    startedAt?: string;
    submittedAt?: string;
    gradedAt?: string;
    feedback: TaskFeedback | null;
  };
}

export interface PendingSubmission {
  id: string;
  mentee: {
    firstName: string;
    lastName: string;
    email: string;
    region: string;
    ministryRole: string;
  };
  task: {
    id?: string;
    title: string;
    taskNumber: number;
    quarter: QuarterKey;
    description?: string;
    instructions?: TaskInstruction[];
    minWords?: number;
    dueDate?: string;
  };
  quarter: QuarterKey;
  status: TaskStatus;
  submittedAt?: string;
  wordCount?: number;
  isLate?: boolean;
  draftContent?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  feedback: TaskFeedback | null;
}

interface TaskListResponse {
  success: boolean;
  message: string;
  count: number;
  data: TaskWithSubmission[];
}

interface TaskItemResponse {
  success: boolean;
  message: string;
  data: TaskWithSubmission;
}

interface SubmissionResponse {
  success: boolean;
  message: string;
  data: TaskWithSubmission["submission"] & { id: string; task: string; quarter: QuarterKey };
}

interface PendingSubmissionsResponse {
  success: boolean;
  message: string;
  count: number;
  data: PendingSubmission[];
}

interface CreateTaskRequest {
  quarter: QuarterKey;
  cycle?: string;
  taskNumber: number;
  title: string;
  description?: string;
  instructions?: TaskInstruction[];
  minWords?: number;
  tags?: string[];
  availableFrom?: string;
  dueDate?: string;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<TaskListResponse, { quarter?: QuarterKey; includeAll?: boolean }>({
      query: (params) => ({ url: "/tasks", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Task" as const, id })),
              { type: "Task", id: "LIST" },
            ]
          : [{ type: "Task", id: "LIST" }],
    }),

    getTask: builder.query<TaskItemResponse, string>({
      query: (id) => `/tasks/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Task", id }],
    }),

    startTask: builder.mutation<SubmissionResponse, string>({
      query: (taskId) => ({ url: `/tasks/${taskId}/start`, method: "POST" }),
      invalidatesTags: (_result, _error, taskId) => [
        { type: "Task", id: taskId },
        { type: "Task", id: "LIST" },
        { type: "TaskSubmission", id: taskId },
      ],
    }),

    saveDraft: builder.mutation<
      SubmissionResponse,
      { taskId: string; draftContent?: string; progressPercent?: number; wordCount?: number }
    >({
      query: ({ taskId, ...body }) => ({
        url: `/tasks/${taskId}/draft`,
        method: "PATCH",
        body,
      }),
    }),

    submitTask: builder.mutation<
      SubmissionResponse,
      {
        taskId: string;
        draftContent?: string;
        wordCount?: number;
        attachmentUrl?: string;
        attachmentName?: string;
      }
    >({
      query: ({ taskId, ...body }) => ({
        url: `/tasks/${taskId}/submit`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Task", id: taskId },
        { type: "Task", id: "LIST" },
        { type: "TaskSubmission", id: taskId },
        { type: "JourneyProfile", id: "CURRENT" },
        { type: "JourneyQuarters", id: "ALL" },
      ],
    }),

    getPendingSubmissions: builder.query<
      PendingSubmissionsResponse,
      { quarter?: QuarterKey }
    >({
      query: (params) => ({ url: "/tasks/submissions/pending", params }),
      providesTags: [{ type: "TaskSubmission", id: "PENDING" }],
    }),

    gradeSubmission: builder.mutation<
      SubmissionResponse,
      { submissionId: string; comment: string; score: number }
    >({
      query: ({ submissionId, ...body }) => ({
        url: `/tasks/submissions/${submissionId}/grade`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "TaskSubmission", id: "PENDING" },
        { type: "Task", id: "LIST" },
        { type: "JourneyProfile", id: "CURRENT" },
        { type: "JourneyQuarters", id: "ALL" },
      ],
    }),

    createTask: builder.mutation<TaskItemResponse, CreateTaskRequest>({
      query: (body) => ({ url: "/tasks/definitions", method: "POST", body }),
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    updateTask: builder.mutation<
      TaskItemResponse,
      { id: string } & Partial<CreateTaskRequest & { isPublished: boolean; isActive: boolean }>
    >({
      query: ({ id, ...body }) => ({
        url: `/tasks/definitions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Task", id },
        { type: "Task", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useStartTaskMutation,
  useSaveDraftMutation,
  useSubmitTaskMutation,
  useGetPendingSubmissionsQuery,
  useGradeSubmissionMutation,
  useCreateTaskMutation,
  useUpdateTaskMutation,
} = tasksApi;